import { JWTRole, useAuth, JWT } from "@hackthenorth/north";
import React, {
  createContext,
  useContext,
  useMemo,
  useEffect,
  useCallback,
} from "react";
import { useNavigate } from "react-router";

import { client } from "src/api/client";
import { CURRENT_EVENT_SLUG, EVENT_ID } from "src/shared/constants/event";
import { BaseRoute } from "src/shared/constants/route";

import { useGetMeLazyQuery } from "./graphql/getMe.generated";

export interface Info {
  name: string;
  legalName: string | null;
  dateOfBirth: string | null;
  phoneNumber: string | null;
  email: string;
  event_platform_user_id: string;
}

export interface RegInfo {
  discordRegistered: boolean;
}

interface UserContextState {
  id: number | null;
  info: Info | null;
  roles: JWTRole[];
  isAuthenticated: boolean;
  isOrganizer: boolean;
  email: string | null;
  userFound: boolean | null;
  attendeeFound: boolean | null;

  logOut: () => void;
  logIn: () => void;
  logInToPath: (path: string) => void;
  signInAgain: () => void;
  error?: string;
}

const DEFAULT_STATE: UserContextState = {
  id: null,
  roles: [],
  info: null,
  isAuthenticated: false,
  isOrganizer: false,
  email: null,
  userFound: null,
  attendeeFound: null,

  logOut: () => {},
  logIn: () => {},
  logInToPath: () => {},
  signInAgain: () => {},
};

const UserContext: React.Context<UserContextState> =
  createContext(DEFAULT_STATE);

export const useUserContext = () => useContext(UserContext);

export const UserContextProvider: React.FC = ({ children }) => {
  const navigate = useNavigate();
  const { token, logOut: authLogOut, tokenExpired, loginUrl } = useAuth();
  const [getMe, { data, error }] = useGetMeLazyQuery();

  // wait to fetch the user since the query will not re-run even if token changes
  useEffect(() => {
    if (token) getMe({ variables: { eventId: EVENT_ID } });
  }, [token, getMe]);

  /**
   * Build UserState
   */
  const id: UserContextState["id"] = useMemo(() => token?.id ?? null, [token]);

  const roles: UserContextState["roles"] = useMemo(
    () => token?.roles[CURRENT_EVENT_SLUG] ?? [],
    [token]
  );

  const isAuthenticated: UserContextState["isAuthenticated"] = useMemo(
    () => !!token,
    [token]
  );

  const isOrganizer: UserContextState["isOrganizer"] = useMemo(
    () => !!token?.is_organizer,
    [token]
  );

  const email: UserContextState["email"] = useMemo(
    () => (token as JWT | null)?.email ?? null,
    [token]
  );

  const userFound: UserContextState["userFound"] = data
    ? true
    : error
    ? false
    : null;

  const attendeeFound: UserContextState["attendeeFound"] = data
    ? (data.me._count?.event_attendees ?? 0) > 0
    : error
    ? false
    : null;

  const info: UserContextState["info"] = useMemo(
    () =>
      data && isAuthenticated
        ? {
            name: data.me.name,
            legalName: data.me.legal_name ?? null,
            dateOfBirth: data.me.date_of_birth ?? null,
            phoneNumber: data.me.phone_number ?? null,
            email: email ?? data.me.email,
            event_platform_user_id: data.me.event_platform_user_id ?? "",
          }
        : null,
    [data, email, isAuthenticated]
  );

  const logOut: UserContextState["logOut"] = useCallback(() => {
    authLogOut();
    navigate(BaseRoute.HOME);
    client.cache.reset();
  }, [navigate, authLogOut]);

  const logIn: UserContextState["logIn"] = () => {
    window.location.href = loginUrl();
  };

  // This must be a separate function to avoid breaking past use cases of logIn
  // Adding `path` as an optional parameter to logIn
  // would for example break <Button onClick={logIn} ...
  // as some weird click event gets passed in as the first parameter
  // I didn't feel like just doing type checking for string bc that feels less safe
  const logInToPath: UserContextState["logInToPath"] = (path: string) => {
    window.location.href = loginUrl(path);
  };

  const signInAgain: UserContextState["signInAgain"] = useCallback(() => {
    authLogOut();
    client.cache.reset();
    window.location.href = loginUrl();
  }, [authLogOut, loginUrl]);

  useEffect(() => {
    // Log the user out if JWT is invalid
    if (tokenExpired) logOut();
  }, [token, logOut, tokenExpired]);

  /**
   * Build state
   */
  const userState: UserContextState = {
    id,
    info,
    roles,
    isAuthenticated,
    isOrganizer,
    email,
    userFound,
    attendeeFound,
    logOut,
    logIn,
    logInToPath,
    signInAgain,
    error: error?.message,
  };

  return (
    <UserContext.Provider value={userState}>{children}</UserContext.Provider>
  );
};
